import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Category, Item } from '../types/index';
import {
  CATALOG_CATEGORIES,
  CATALOG_ITEMS,
  catalogItemDocumentId
} from './catalogConstants';

export { CATALOG_CATEGORIES, CATALOG_ITEMS, catalogItemDocumentId };

export type CatalogLang = 'en' | 'pt' | 'fr';

export interface CatalogCategoryFirestore {
  id: string;
  route: string;
  image: string;
  position: number;
  title_pt: string;
  title_en: string;
  title_fr: string;
  description_pt: string;
  description_en: string;
  description_fr: string;
  created_at?: Timestamp;
  updated_at?: Timestamp;
}

export interface CatalogSpecialOfferFirestore {
  type_pt: string;
  type_en: string;
  type_fr: string;
  description_pt: string[];
  description_en: string[];
  description_fr: string[];
}

export interface CatalogItemFirestore {
  categoryId: string;
  itemId: number;
  position: number;
  isVisible: boolean;
  name_pt: string;
  name_en: string;
  name_fr: string;
  description_pt: string;
  description_en: string;
  description_fr: string;
  openingHours_pt: { day: string; hours: string }[];
  openingHours_en: { day: string; hours: string }[];
  openingHours_fr: { day: string; hours: string }[];
  email: string;
  logo: string;
  mapsUrl: string;
  address: string;
  phone: string;
  locationIds: number[];
  socialMedia: { facebook: string; instagram: string; website: string };
  rootFolder?: string;
  images?: string[];
  menuUrl?: string;
  redirectUrl?: string;
  cuisine?: string;
  price?: string;
  duration?: string;
  type?: string;
  services?: string[];
  cardInfo?: string;
  priceRange?: number;
  specialOffer?: CatalogSpecialOfferFirestore;
  created_at?: Timestamp;
  updated_at?: Timestamp;
}

export function itemFirestoreToItem(data: CatalogItemFirestore, lang: CatalogLang): Item {
  const name =
    lang === 'pt' ? data.name_pt || data.name_en : lang === 'fr' ? data.name_fr || data.name_en : data.name_en;
  const description =
    lang === 'pt'
      ? data.description_pt || data.description_en
      : lang === 'fr'
        ? data.description_fr || data.description_en
        : data.description_en;
  const hoursKey = lang === 'pt' ? 'openingHours_pt' : lang === 'fr' ? 'openingHours_fr' : 'openingHours_en';
  const openingHours =
    (data[hoursKey] as { day: string; hours: string }[])?.length > 0
      ? (data[hoursKey] as { day: string; hours: string }[])
      : data.openingHours_en || [];

  let specialOffer: Item['specialOffer'];
  if (data.specialOffer) {
    const so = data.specialOffer;
    const type =
      lang === 'pt' ? so.type_pt || so.type_en : lang === 'fr' ? so.type_fr || so.type_en : so.type_en;
    const desc =
      lang === 'pt'
        ? so.description_pt?.length
          ? so.description_pt
          : so.description_en
        : lang === 'fr'
          ? so.description_fr?.length
            ? so.description_fr
            : so.description_en
          : so.description_en;
    specialOffer = { type, description: desc || [] };
  }

  return {
    id: data.itemId,
    name,
    email: data.email || '',
    logo: data.logo || '',
    description,
    locationIds: Array.isArray(data.locationIds) ? data.locationIds : [],
    mapsUrl: data.mapsUrl || '',
    openingHours,
    address: data.address || '',
    phone: data.phone || '',
    isVisible: data.isVisible !== false,
    socialMedia: {
      facebook: data.socialMedia?.facebook || '',
      instagram: data.socialMedia?.instagram || '',
      website: data.socialMedia?.website || ''
    },
    specialOffer,
    priceRange: data.priceRange,
    cardInfo: data.cardInfo,
    cuisine: data.cuisine,
    price: data.price,
    duration: data.duration,
    type: data.type,
    services: data.services,
    rootFolder: data.rootFolder,
    images: data.images,
    menuUrl: data.menuUrl,
    redirectUrl: data.redirectUrl
  };
}

function categoryFirestoreToCategory(
  cat: CatalogCategoryFirestore,
  items: CatalogItemFirestore[],
  lang: CatalogLang
): Category {
  const title =
    lang === 'pt' ? cat.title_pt || cat.title_en : lang === 'fr' ? cat.title_fr || cat.title_en : cat.title_en;
  const description =
    lang === 'pt'
      ? cat.description_pt || cat.description_en
      : lang === 'fr'
        ? cat.description_fr || cat.description_en
        : cat.description_en;

  const sorted = [...items].sort((a, b) => {
    const pa = a.position ?? a.itemId;
    const pb = b.position ?? b.itemId;
    return pa - pb;
  });

  return {
    id: cat.id,
    title,
    route: cat.route,
    description,
    image: cat.image,
    items: sorted.map((it) => itemFirestoreToItem(it, lang))
  };
}

/** Public site: localized categories with all items (visibility filtered by caller). */
export async function loadLocalizedCatalog(): Promise<Record<CatalogLang, Category[]> | null> {
  try {
    const catSnap = await getDocs(collection(db, CATALOG_CATEGORIES));
    if (catSnap.empty) {
      return null;
    }

    const itemSnap = await getDocs(collection(db, CATALOG_ITEMS));
    const itemsByCat = new Map<string, CatalogItemFirestore[]>();
    itemSnap.forEach((d) => {
      const row = d.data() as CatalogItemFirestore;
      if (!row.categoryId) return;
      const list = itemsByCat.get(row.categoryId) || [];
      list.push(row);
      itemsByCat.set(row.categoryId, list);
    });

    const catRows: CatalogCategoryFirestore[] = catSnap.docs
      .map((d) => {
        const data = d.data() as CatalogCategoryFirestore;
        return { ...data, id: data.id || d.id };
      })
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    const en = catRows.map((c) => categoryFirestoreToCategory(c, itemsByCat.get(c.id) || [], 'en'));
    const pt = catRows.map((c) => categoryFirestoreToCategory(c, itemsByCat.get(c.id) || [], 'pt'));
    const fr = catRows.map((c) => categoryFirestoreToCategory(c, itemsByCat.get(c.id) || [], 'fr'));

    return { en, pt, fr };
  } catch (e) {
    console.error('loadLocalizedCatalog:', e);
    return null;
  }
}

export async function getAdminCategoryDocs(): Promise<CatalogCategoryFirestore[]> {
  const catSnap = await getDocs(collection(db, CATALOG_CATEGORIES));
  return catSnap.docs
    .map((d) => {
      const data = d.data() as CatalogCategoryFirestore;
      return { ...data, id: data.id || d.id };
    })
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

export async function getAdminItemDocsForCategory(categoryId: string): Promise<CatalogItemFirestore[]> {
  const itemSnap = await getDocs(collection(db, CATALOG_ITEMS));
  const rows: CatalogItemFirestore[] = [];
  itemSnap.forEach((d) => {
    const row = d.data() as CatalogItemFirestore;
    if (row.categoryId === categoryId) rows.push(row);
  });
  return rows.sort((a, b) => (a.position ?? a.itemId) - (b.position ?? b.itemId));
}

export async function getAdminItemDoc(categoryId: string, itemId: number): Promise<CatalogItemFirestore | null> {
  const ref = doc(db, CATALOG_ITEMS, catalogItemDocumentId(categoryId, itemId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() as CatalogItemFirestore;
}

export async function getNextItemId(categoryId: string): Promise<number> {
  const items = await getAdminItemDocsForCategory(categoryId);
  if (items.length === 0) return 1;
  return Math.max(...items.map((i) => i.itemId)) + 1;
}

export async function saveCategory(data: CatalogCategoryFirestore): Promise<void> {
  const now = Timestamp.now();
  const ref = doc(db, CATALOG_CATEGORIES, data.id);
  const prev = await getDoc(ref);
  const created = prev.exists() ? (prev.data() as CatalogCategoryFirestore).created_at : now;
  await setDoc(ref, {
    ...data,
    created_at: created,
    updated_at: now
  });
}

export async function deleteCategory(categoryId: string): Promise<void> {
  const items = await getAdminItemDocsForCategory(categoryId);
  const batch = writeBatch(db);
  items.forEach((it) => {
    batch.delete(doc(db, CATALOG_ITEMS, catalogItemDocumentId(categoryId, it.itemId)));
  });
  batch.delete(doc(db, CATALOG_CATEGORIES, categoryId));
  await batch.commit();
}

export async function saveItem(data: CatalogItemFirestore): Promise<void> {
  const now = Timestamp.now();
  const id = catalogItemDocumentId(data.categoryId, data.itemId);
  const ref = doc(db, CATALOG_ITEMS, id);
  const prev = await getDoc(ref);
  const created = prev.exists() ? (prev.data() as CatalogItemFirestore).created_at : now;
  await setDoc(ref, {
    ...data,
    created_at: created,
    updated_at: now
  });
}

export async function deleteItem(categoryId: string, itemId: number): Promise<void> {
  await deleteDoc(doc(db, CATALOG_ITEMS, catalogItemDocumentId(categoryId, itemId)));
}

export function emptyCategory(partial: Partial<CatalogCategoryFirestore> & { id: string }): CatalogCategoryFirestore {
  return {
    id: partial.id,
    route: partial.route || `/${partial.id}`,
    image: partial.image || '',
    position: partial.position ?? 0,
    title_pt: partial.title_pt || '',
    title_en: partial.title_en || '',
    title_fr: partial.title_fr || '',
    description_pt: partial.description_pt || '',
    description_en: partial.description_en || '',
    description_fr: partial.description_fr || ''
  };
}

export function emptyItem(
  categoryId: string,
  itemId: number,
  partial?: Partial<CatalogItemFirestore>
): CatalogItemFirestore {
  const emptyHours: { day: string; hours: string }[] = [];
  return {
    categoryId,
    itemId,
    position: partial?.position ?? itemId,
    isVisible: partial?.isVisible !== false,
    name_pt: partial?.name_pt || '',
    name_en: partial?.name_en || '',
    name_fr: partial?.name_fr || '',
    description_pt: partial?.description_pt || '',
    description_en: partial?.description_en || '',
    description_fr: partial?.description_fr || '',
    openingHours_pt: partial?.openingHours_pt || emptyHours,
    openingHours_en: partial?.openingHours_en || emptyHours,
    openingHours_fr: partial?.openingHours_fr || emptyHours,
    email: partial?.email || '',
    logo: partial?.logo || '',
    mapsUrl: partial?.mapsUrl || '',
    address: partial?.address || '',
    phone: partial?.phone || '',
    locationIds: partial?.locationIds || [],
    socialMedia: partial?.socialMedia || { facebook: '', instagram: '', website: '' },
    rootFolder: partial?.rootFolder,
    images: partial?.images || [],
    menuUrl: partial?.menuUrl,
    redirectUrl: partial?.redirectUrl,
    cuisine: partial?.cuisine,
    price: partial?.price,
    duration: partial?.duration,
    type: partial?.type,
    services: partial?.services,
    cardInfo: partial?.cardInfo,
    priceRange: partial?.priceRange,
    specialOffer: partial?.specialOffer
  };
}

export function notifyCatalogUpdated(): void {
  window.dispatchEvent(new Event('catalog-updated'));
}
